import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {AngularSvgIconModule} from 'angular-svg-icon';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {PaginationComponent} from '@components/ui/pagination/pagination.component';
import {PageData} from '@components/ui/pagination/models/page-data.model';
import {TableCellTextComponent} from '@components/ui/table-template/cells/table-cell-text/table-cell-text.component';
import {TableCellDateComponent} from '@components/ui/table-template/cells/table-cell-date/table-cell-date.component';
import {TableCellDateTimeComponent} from '@components/ui/table-template/cells/table-cell-date-time/table-cell-date-time.component';
import {TableCellIconComponent} from '@components/ui/table-template/cells/table-cell-icon/table-cell-icon.component';
import {TableCellButtonComponent} from '@components/ui/table-template/cells/table-cell-button/table-cell-button.component';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {InputSearchComponent} from '@components/ui/input-search/input-search.component';
import {CellClicked} from '@components/ui/table-template/models/cell-clicked';
import {TableCellEmptyComponent} from '@components/ui/table-template/cells/table-cell-empty/table-cell-empty.component';
import {TableCellDirective} from '@components/ui/table-template/directives/table-cell.directive';

@Component({
    selector: 'app-table-template',
    templateUrl: './table-template.component.html',
    styleUrls: ['./table-template.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [InputSearchComponent, PaginationComponent, TableCellDirective, AngularSvgIconModule]
})
export class TableTemplateComponent implements OnChanges {
  readonly tableTitle = input<string>();
  readonly tableTitleId = input('title');
  readonly showTitle = input(false);
  readonly showSearch = input(true);
  readonly showToggle = input(false);
  readonly showPagination = input(true);

  readonly hasCardView = input(false);

  readonly clickableRows = input<boolean>();
  readonly columns = input<TableTemplateColumnConfig[]>();
  readonly currentPage = input<PageViewModel<DataViewModel>>();

  readonly sortedOn = input<string>();
  readonly sortDescending = input<boolean>();

  readonly tableMinWidthInRem = input<number>();

  paginationPageData = signal(new PageData(0, 20));
  paginationCurrentPage = signal(1);

  readonly sortChanged = output<TableTemplateColumnConfig>();
  readonly searchChanged = output<string>();
  readonly pageChanged = output<number>();
  readonly rowClicked = output<DataViewModel>();
  readonly cellClicked = output<CellClicked>();

  @ViewChild(PaginationComponent) pagination: PaginationComponent;
  @ViewChild(InputSearchComponent) inputSearch: InputSearchComponent;

  expandSearch = signal(false);
  viewType = signal(2);

  VIEW_TYPE_TILE = 1;
  VIEW_TYPE_GRID = 2;

  ngOnChanges(changes: SimpleChanges): void {
    const currentPageChanges = changes.currentPage;

    if (currentPageChanges !== undefined) {
      const currentPage: PageViewModel<DataViewModel> = currentPageChanges.currentValue;
      this.paginationPageData.set(new PageData(currentPage.totalElements, currentPage.pageSize));
      this.paginationCurrentPage.set(currentPage.pageNumber);
    }
  }

  sort(column: TableTemplateColumnConfig) {
    if (column.sortable) {
      this.sortChanged.emit(column);
    }
  }

  onColumnKeyDown(event: KeyboardEvent, column: TableTemplateColumnConfig): void {
    // Handle Enter and Space key presses for sortable columns
    if ((event.key === 'Enter' || event.key === ' ') && column.sortable) {
      event.preventDefault();
      this.sort(column);
    }
  }

  onRowKeyDown(event: KeyboardEvent, viewModel: DataViewModel): void {
    // Handle Enter and Space key presses for clickable rows
    if ((event.key === 'Enter' || event.key === ' ') && this.clickableRows()) {
      event.preventDefault();
      this.click(viewModel);
    }
  }

  search(searchTerm: string) {
    this.searchChanged.emit(searchTerm);
  }

  page(targetPage: number) {
    this.pageChanged.emit(targetPage);
  }

  click(vm: DataViewModel) {
    if (this.clickableRows()) {
      this.rowClicked.emit(vm);
    }
  }

  loadInTableCell(viewContainerRef: ViewContainerRef, column: TableTemplateColumnConfig, row: DataViewModel): void {
    if (column.emptyFallback && !row[column.dataProperty]) {
      const viewContainerRefEmpty = viewContainerRef.createComponent(TableCellEmptyComponent);
      viewContainerRefEmpty.instance.initialize(column, row, row[column.dataProperty]);
      return;
    }

    switch (column.cellType) {
      case CellType.Text: {
        const viewContainerRefText = viewContainerRef.createComponent(TableCellTextComponent);
        viewContainerRefText.instance.initialize(column, row, row[column.dataProperty]);
        return;
      }
      case CellType.Date: {
        const viewContainerRefDate = viewContainerRef.createComponent(TableCellDateComponent);
        viewContainerRefDate.instance.initialize(column, row, row[column.dataProperty]);
        return;
      }
      case CellType.DateTime: {
        const viewContainerRefDateTime = viewContainerRef.createComponent(TableCellDateTimeComponent);
        viewContainerRefDateTime.instance.initialize(column, row, row[column.dataProperty]);
        return;
      }
      case CellType.Icon: {
        const viewContainerRefIcon = viewContainerRef.createComponent(TableCellIconComponent);
        viewContainerRefIcon.instance.initialize(column, row, row[column.dataProperty]);
        return;
      }
      case CellType.Button: {
        const viewContainerRefButton = viewContainerRef.createComponent(TableCellButtonComponent);
        viewContainerRefButton.instance.initialize(column, row, row[column.dataProperty]);
        viewContainerRefButton.instance.buttonClickEvent.subscribe(event => {
          this.cellClicked.emit(event);
        });
        return;
      }
    }
  }

  getColumnClass(column: TableTemplateColumnConfig): string {
    switch (column.alignment) {
      case Alignment.Left:
        return 'align-left';
      case Alignment.Center:
        return 'align-center';
      case Alignment.Right:
        return 'align-right';
    }
  }

  showSearchBar() {
    this.expandSearch.set(true);
    this.inputSearch.focus();
  }

  hideSearchBar() {
    this.expandSearch.set(false);
  }

  createLabel(column: TableTemplateColumnConfig): string {
    let label = '';
    if (column.sortable) {
      label += 'sortable ';
    }
    label += 'column ' + column.title;
    return label;
  }

  getColumnClassSorted(column: TableTemplateColumnConfig): boolean {
    return this.sortedOn() === column.dataProperty;
  }

  getColumnClassDescending(column: TableTemplateColumnConfig): boolean {
    return this.sortedOn() === column.dataProperty && this.sortDescending();
  }

  getColumnAttributeAriaSort(column: TableTemplateColumnConfig): string | undefined {
    if (this.sortedOn() !== column.dataProperty) {
      return undefined;
    }

    if (this.sortDescending()) {
      return 'descending';
    } else {
      return 'ascending';
    }
  }
}
